import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '@/components/Seo';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { getMunicipalities, getAllMunicipalities, Municipality, FEE_CATEGORIES, sumFees } from '@/lib/data';
import { useFeeCategories } from '@/contexts/FeeCategoryContext';
import { ArrowLeft, CheckCircle2, Filter, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import ComparisonSelector from '@/components/ComparisonSelector';

const ComparisonPage = () => {
  const { enabledCategories } = useFeeCategories();
  const visibleCategories = FEE_CATEGORIES.filter(c => enabledCategories.includes(c.id));
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMunicipalities, setSelectedMunicipalities] = useState<Municipality[]>([]);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Bare hent kommuner hvis vi ikke allerede har dem
        if (!municipalities.length) {
          setIsLoading(true);
          const municipalities = await getAllMunicipalities();
          setMunicipalities(municipalities);
        }
      } catch (error) {
        console.error('Feil ved henting av kommunedata for sammenligning:', error);
        toast.error('Kunne ikke hente kommune-data. Vennligst prøv igjen senere.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);
  
  const filteredMunicipalities = municipalities.filter(
    m => m.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleToggleSelect = (municipality: Municipality) => {
    if (selectedMunicipalities.some(m => m.id === municipality.id)) {
      setSelectedMunicipalities(selectedMunicipalities.filter(m => m.id !== municipality.id));
    } else {
      if (selectedMunicipalities.length < 5) {
        setSelectedMunicipalities([...selectedMunicipalities, municipality]);
      } else {
        toast.warning("Du kan velge maksimalt 5 kommuner for sammenligning.");
      }
    }
  };
  
  const sortedMunicipalities = [...filteredMunicipalities].sort((a, b) => {
    if (sortBy === 'name') {
      return sortOrder === 'asc' 
        ? a.name.localeCompare(b.name) 
        : b.name.localeCompare(a.name);
    } else if (sortBy === 'total') {
      const totalA = sumFees(a.currentFees, enabledCategories);
      const totalB = sumFees(b.currentFees, enabledCategories);
      return sortOrder === 'asc' ? totalA - totalB : totalB - totalA;
    } else {
      // Sort by specific fee type
      const feeA = a.currentFees.find(fee => fee.id === sortBy)?.amount || 0;
      const feeB = b.currentFees.find(fee => fee.id === sortBy)?.amount || 0;
      return sortOrder === 'asc' ? feeA - feeB : feeB - feeA;
    }
  });
  
  const toggleSortOrder = (column: string) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };
  
  return (
    <div className="min-h-screen flex flex-col bg-softWhite">
      <Seo
        title="Sammenlign kommuner | Kommunale avgifter i Norge"
        description="Velg opptil 5 kommuner å sammenligne. Se vann, avløp, renovasjon og septiktømming side om side. Sorter etter avgiftstype."
        path="/sammenlign"
      />
      <Header />
      
      <main className="flex-grow container mx-auto px-4 py-8">
        {/* Hero – samme kortstil som forsiden */}
        <div className="mb-8 py-6 px-5 md:px-6 rounded-2xl bg-gradient-to-br from-nordicBlue/5 to-transparent border border-nordicBlue/10">
          <nav className="flex items-center gap-2 text-sm text-warmGray mb-4">
            <Link to="/" className="flex items-center gap-1.5 hover:text-nordicBlue transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" />
            Forside
          </Link>
            <span>/</span>
            <span className="text-nordicBlue font-semibold">Sammenlign</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-bold text-deepCharcoal mb-2">Sammenlign kommunale avgifter</h1>
          <p className="text-warmGray">
            Velg opptil 5 kommuner å sammenligne. Sortér etter ulike avgiftstyper for enkel sammenligning.
          </p>
        </div>
        
        {/* Comparison Selector Component */}
        <div className="mb-8">
          <ComparisonSelector 
            selectedMunicipalities={selectedMunicipalities}
            onMunicipalitySelect={(municipality) => {
              if (selectedMunicipalities.length < 5) {
                setSelectedMunicipalities([...selectedMunicipalities, municipality]);
              } else {
                toast.warning("Du kan velge maksimalt 5 kommuner for sammenligning.");
              }
            }}
            onMunicipalityRemove={(municipalityId) => {
              setSelectedMunicipalities(
                selectedMunicipalities.filter(m => m.id !== municipalityId)
              );
            }}
          />
        </div>
        
        {/* Search and filter */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="relative flex-grow">
            <Search className="absolute left-3 top-3 h-5 w-5 text-warmGray" />
            <Input 
              type="text" 
              placeholder="Søk etter kommune..." 
              className="pl-10 py-6 pr-4 bg-white w-full"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          
          <div className="flex gap-2">
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Sorter etter" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Kommunenavn</SelectItem>
                <SelectItem value="total">Totale avgifter</SelectItem>
                {visibleCategories.map((category) => (
                  <SelectItem key={category.id} value={category.id}>{category.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            >
              {sortOrder === 'asc' ? 'Stigende' : 'Synkende'}
            </Button>
          </div>
        </div>
        
        {/* Selected municipalities */}
        {selectedMunicipalities.length > 0 && (
          <div className="mb-8 bg-white p-4 rounded-lg shadow-sm border border-slate-100">
            <h2 className="text-lg font-semibold mb-4">Valgte kommuner for sammenligning</h2>
            <div className="flex flex-wrap gap-2">
              {selectedMunicipalities.map(municipality => (
                <div 
                  key={municipality.id}
                  className="bg-nordicBlue/10 text-nordicBlue rounded-full px-3 py-1 flex items-center gap-1 cursor-pointer"
                  onClick={() => handleToggleSelect(municipality)}
                >
                  <CheckCircle2 className="h-4 w-4" />
                  <span>{municipality.name}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <Button 
                variant="default" 
                disabled={selectedMunicipalities.length < 2}
                onClick={() => {
                  // Navigate to comparison view
                  const ids = selectedMunicipalities.map(m => m.id).join(',');
                  window.open(`/sammenligne-detalj?ids=${ids}`, '_blank');
                }}
              >
                Se detaljert sammenligning
              </Button>
            </div>
          </div>
        )}
        
        {/* Municipality table */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-100 overflow-hidden">
          {isLoading ? (
            <div className="p-4">
              <Skeleton className="h-10 w-full mb-4" />
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full mb-2" />
              ))}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead 
                    className="cursor-pointer hover:text-nordicBlue"
                    onClick={() => toggleSortOrder('name')}
                  >
                    Kommune {sortBy === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableHead>
                  {visibleCategories.map((category) => (
                    <TableHead
                      key={category.id}
                      className="cursor-pointer hover:text-nordicBlue text-right"
                      onClick={() => toggleSortOrder(category.id)}
                    >
                      {category.name} {sortBy === category.id && (sortOrder === 'asc' ? '↑' : '↓')}
                    </TableHead>
                  ))}
                  <TableHead
                    className="cursor-pointer hover:text-nordicBlue text-right"
                    onClick={() => toggleSortOrder('total')}
                  >
                    Totalt {sortBy === 'total' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedMunicipalities.map((municipality) => {
                  const isSelected = selectedMunicipalities.some(m => m.id === municipality.id);
                  const totalFees = sumFees(municipality.currentFees, enabledCategories);

                  return (
                    <TableRow
                      key={municipality.id}
                      className={`cursor-pointer ${isSelected ? 'bg-nordicBlue/10' : ''}`}
                      onClick={() => handleToggleSelect(municipality)}
                    >
                      <TableCell>
                        <div className={`h-5 w-5 rounded-full border flex items-center justify-center ${
                          isSelected ? 'bg-nordicBlue text-white border-nordicBlue' : 'border-gray-300'
                        }`}>
                          {isSelected && <CheckCircle2 className="h-4 w-4" />}
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{municipality.name}</TableCell>
                      {visibleCategories.map((category) => (
                        <TableCell key={category.id} className="text-right">
                          {(municipality.currentFees.find(fee => fee.id === category.id)?.amount || 0).toLocaleString('nb-NO')}
                        </TableCell>
                      ))}
                      <TableCell className="text-right font-bold">{totalFees.toLocaleString('nb-NO')}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </div>
        
        {/* Note */}
        <p className="text-sm text-warmGray mt-4">
          Alle beløp er oppgitt i norske kroner (NOK) per år for en bolig på 120m². Kilde: Statistisk sentralbyrå (SSB).
        </p>
      </main>
      
      <Footer />
    </div>
  );
};

export default ComparisonPage;
